import { useEffect, useRef, useState } from "react";
import * as InputSelectStyles from "./InputSelect.styles";
import { InputProps } from "src/components/UI";

type Option = {
  label: string;
  value: any;
};

type InputSelectProps = InputProps & {
  options?: Array<Option>;
  render?: (option: Option) => string;
  selectedOption: Option["value"];
  onSelect: (value: Option["value"]) => void;
};

const InputSelect = (props: InputSelectProps) => {
  const {
    options = [],
    render,
    selectedOption,
    onSelect,
    ...restProps
  } = props;
  const [search, setSearch] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputSelectRef = useRef<HTMLDivElement>(null);
  const [filteredOptions, setFilteredOptions] = useState(options);

  const handleSelect = (option: any) => {
    setSearch(render(option));
    onSelect(option);
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSearch(value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputSelectRef.current &&
        !inputSelectRef.current.contains(event.target as Node)
      ) {
        if (selectedOption) {
          setSearch(render(selectedOption));
        }
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputSelectRef, render, selectedOption]);

  useEffect(() => {
    if (!search) {
      setFilteredOptions(options); // If search is empty, show all options
      return;
    }

    // Perform case-insensitive search using includes
    const filteredOptionsBySearch = options.filter(
      (option) => option.label.toLowerCase().includes(search.toLowerCase()) // Case-insensitive contains check
    );

    setFilteredOptions(filteredOptionsBySearch);
  }, [search, options]); // Re-run when `search` or `options` change

  // prefill search with selectedOption label
  useEffect(() => {
    if (!isFocused && selectedOption) {
      setSearch(render(selectedOption));
    }
  }, [selectedOption, isFocused, render]);

  return (
    <InputSelectStyles.Container ref={inputSelectRef}>
      <InputSelectStyles.InputSelect
        {...restProps}
        value={search}
        autoComplete="off"
        onChange={handleChange}
        onFocus={handleFocus}
      />
      {isFocused && (
        <InputSelectStyles.SelectionList className="input-select-list">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <InputSelectStyles.SelectionItem
                key={index}
                onClick={() => handleSelect(option)}
              >
                {render ? render(option) : option.label}
              </InputSelectStyles.SelectionItem>
            ))
          ) : (
            <InputSelectStyles.NoSelectionList className="input-select-list">
              Tidak ada data
            </InputSelectStyles.NoSelectionList>
          )}
        </InputSelectStyles.SelectionList>
      )}
    </InputSelectStyles.Container>
  );
};

export default InputSelect;
